<template>
  <!-- attach: デフォルトのapp直下detachだとスクロールするドロワー内で位置がずれるため、カード直下に描画する
       z-index: attach時はVuetifyの自動採番が効かず、後続のQRボタン行等(position:relative)が上に重なるため明示する -->
  <v-menu
    v-model="isOpen"
    :close-on-content-click="false"
    attach
    offset-y
    max-width="360"
    min-width="320"
    z-index="8"
  >
    <template #activator="{ on, attrs }">
      <v-card
        class="customer-detail-card mt-2 pa-3"
        outlined
        v-bind="attrs"
        v-on="on"
      >
        <div
          v-if="isLoading"
          class="text-caption text--secondary"
        >
          読み込み中...
        </div>

        <div
          v-else-if="loadFailed"
          class="text-caption text--secondary"
        >
          顧客情報を取得できませんでした
        </div>

        <template v-else>
          <div class="d-flex align-center">
            <span class="font-weight-bold">{{ customerAttrs.name }}</span>

            <span
              v-if="customerAttrs.memberNumber"
              class="text-caption text--secondary ml-2"
            >
              No.{{ customerAttrs.memberNumber }}
            </span>

            <span class="text-caption text--secondary ml-auto text-right customer-detail-card__visit">
              来店{{ customerAttrs.visitCount || 0 }}回
              <template v-if="lastVisitedAtText">
                <br>最終 {{ lastVisitedAtText }}
              </template>
            </span>
          </div>

          <div
            v-if="latestCaution"
            class="caution-strip mt-2"
          >
            <v-icon
              small
              class="caution-strip__icon"
            >
              {{ icons.mdiAlert }}
            </v-icon>

            <span class="caution-strip__content">{{ latestCaution.content }}</span>

            <span class="text-caption text--secondary ml-1">{{ formatDate(latestCaution.createdAt) }}</span>
          </div>

          <div
            v-else
            class="text-caption text--secondary mt-1"
          >
            注意事項なし ・ タップで詳細
          </div>
        </template>
      </v-card>
    </template>

    <v-card class="customer-detail-popover pa-4">
      <div class="text-subtitle-1 font-weight-bold">
        {{ customerAttrs.name }}
      </div>

      <div class="text-caption text--secondary">
        <span v-if="customerAttrs.memberNumber">会員No.{{ customerAttrs.memberNumber }}</span>
        <span v-if="customerAttrs.receiptAddressee"> ・ 宛名: {{ customerAttrs.receiptAddressee }}</span>
      </div>

      <div class="d-flex mt-3">
        <div class="customer-detail-card__stat">
          <div class="text-caption text--secondary">
            来店回数
          </div>
          <div class="font-weight-medium">
            {{ customerAttrs.visitCount || 0 }}回
          </div>
        </div>

        <div class="customer-detail-card__stat">
          <div class="text-caption text--secondary">
            最終来店
          </div>
          <div class="font-weight-medium">
            {{ lastVisitedAtText || '-' }}
          </div>
        </div>

        <div class="customer-detail-card__stat">
          <div class="text-caption text--secondary">
            誕生日
          </div>
          <div class="font-weight-medium">
            <template v-if="birthdayInfo">
              {{ birthdayInfo.text }}
              <span class="text-caption text--secondary">{{ birthdayInfo.daysUntilText }}</span>
            </template>
            <template v-else>
              -
            </template>
          </div>
        </div>
      </div>

      <template v-if="cautionNotes.length > 0">
        <div class="text-caption caution-title mt-4">
          注意事項
        </div>

        <div
          v-for="note in cautionNotes"
          :key="`caution-${note.id}`"
          class="caution-strip mt-1"
        >
          <div class="caution-strip__body">
            <div>{{ note.content }}</div>
            <div class="text-caption text--secondary">
              {{ noteByline(note) }}
            </div>
          </div>
        </div>
      </template>

      <template v-if="otherNotes.length > 0">
        <div class="text-caption text--secondary mt-4">
          直近のメモ
        </div>

        <div
          v-for="note in otherNotes"
          :key="`note-${note.id}`"
          class="mt-1"
        >
          <div>
            <v-chip
              x-small
              label
              class="mr-1"
            >
              {{ getCategoryLabel(note.category) }}
            </v-chip>
            {{ note.content }}
          </div>
          <div class="text-caption text--secondary">
            {{ noteByline(note) }}
          </div>
        </div>
      </template>

      <div class="text-caption text--secondary mt-4">
        ボトルキープ
      </div>

      <div
        v-if="isBottlesLoading"
        class="text-caption text--secondary"
      >
        読み込み中...
      </div>

      <template v-else-if="displayBottles.length > 0">
        <div
          v-for="bottle in displayBottles"
          :key="`bottle-${bottle.id}`"
          class="d-flex align-center mt-1"
        >
          <v-icon
            small
            class="mr-1"
          >
            {{ icons.mdiBottleWine }}
          </v-icon>

          <span>{{ bottle.attributes.name }}</span>

          <span class="text-caption text--secondary ml-auto">
            <template v-if="bottle.attributes.locationMemo">{{ bottle.attributes.locationMemo }} ・ </template>{{ formatDate(bottle.attributes.keptAt) }}〜
          </span>
        </div>
      </template>

      <div
        v-else
        class="text-caption text--secondary"
      >
        ボトルキープなし
      </div>

      <v-divider class="mt-3" />

      <div class="d-flex justify-end mt-2">
        <v-btn
          text
          small
          color="primary"
          @click="goToCustomerManagement"
        >
          顧客管理で詳細を見る
          <v-icon
            small
            right
          >
            {{ icons.mdiOpenInNew }}
          </v-icon>
        </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import {
  ref, computed, watch, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import { format, differenceInCalendarDays, startOfDay } from 'date-fns'
import { mdiAlert, mdiBottleWine, mdiOpenInNew } from '@mdi/js'
import CustomerApi from '@/api/waiter/Customer'
import CustomerBottleApi from '@/api/waiter/CustomerBottle'
import { NOTE_CATEGORIES } from '@/views/v2-temp/common/customer-management/composables/useCustomerNotes'

export default {
  name: 'CustomerDetailCard',
  props: {
    customerId: {
      type: [Number, String],
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const isOpen = ref(false)
    const isLoading = ref(true)
    const loadFailed = ref(false)
    const customer = ref(null)
    const notes = ref([])
    const bottles = ref([])
    const isBottlesLoading = ref(false)
    const isBottlesFetched = ref(false)

    const customerAttrs = computed(() => customer.value?.attributes || {})

    const cautionNotes = computed(() => {
      return notes.value.filter(note => note.category === 'caution').slice(0, 2)
    })
    const latestCaution = computed(() => cautionNotes.value[0] || null)
    const otherNotes = computed(() => {
      return notes.value.filter(note => note.category !== 'caution').slice(0, 2)
    })
    const displayBottles = computed(() => bottles.value.slice(0, 2))

    const formatDate = dateString => {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return ''

      return format(date, 'M/d')
    }

    const lastVisitedAtText = computed(() => formatDate(customerAttrs.value.lastVisitedAt))

    const noteByline = note => {
      const dateText = formatDate(note.createdAt)

      return note.author?.name ? `${dateText} ・ ${note.author.name}` : dateText
    }

    const birthdayInfo = computed(() => {
      if (!customerAttrs.value.birthday) return null
      const birthday = new Date(customerAttrs.value.birthday)
      if (Number.isNaN(birthday.getTime())) return null

      const today = startOfDay(new Date())
      const nextBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate())
      if (nextBirthday < today) nextBirthday.setFullYear(nextBirthday.getFullYear() + 1)
      const daysUntil = differenceInCalendarDays(nextBirthday, today)

      return {
        text: format(birthday, 'M/d'),
        daysUntilText: daysUntil === 0 ? '本日' : `あと${daysUntil}日`,
      }
    })

    const getCategoryLabel = categoryValue => {
      const category = NOTE_CATEGORIES.find(c => c.value === categoryValue)

      return category ? category.text : 'メモ'
    }

    const fetchDetail = async () => {
      isLoading.value = true
      loadFailed.value = false
      try {
        const [customerRes, notesRes] = await Promise.all([
          CustomerApi.getCustomer(props.customerId),
          CustomerApi.getCustomerNotes(props.customerId, { perPage: 10 }),
        ])
        customer.value = customerRes.data?.data || null
        notes.value = notesRes.data?.notes || []
        if (!customer.value) loadFailed.value = true
      } catch (_e) {
        loadFailed.value = true
      } finally {
        isLoading.value = false
      }
    }

    const fetchBottles = async () => {
      isBottlesLoading.value = true
      try {
        const res = await CustomerBottleApi.getCustomerBottles(props.customerId)
        bottles.value = res.data?.data || []
        isBottlesFetched.value = true
      } catch (_e) {
        bottles.value = []
      } finally {
        isBottlesLoading.value = false
      }
    }

    // ボトルはポップオーバー初回展開時のみ取得
    watch(isOpen, opened => {
      if (opened && !isBottlesFetched.value && !isBottlesLoading.value) fetchBottles()
    })

    const goToCustomerManagement = () => {
      vm.$router.push({
        name: 'waiter-settings-customer-management-show',
        params: {
          club_slug: vm.$route.params.club_slug,
          customerId: String(props.customerId),
        },
      })
    }

    onMounted(fetchDetail)

    return {
      isOpen,
      isLoading,
      loadFailed,
      customerAttrs,
      cautionNotes,
      latestCaution,
      otherNotes,
      displayBottles,
      isBottlesLoading,
      lastVisitedAtText,
      birthdayInfo,
      formatDate,
      noteByline,
      getCategoryLabel,
      goToCustomerManagement,
      icons: {
        mdiAlert,
        mdiBottleWine,
        mdiOpenInNew,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.customer-detail-card {
  cursor: pointer;
}

// 3カラムOverviewは配下の.v-cardを透明化(::v-deep+!important)するため、
// ポップオーバーはより高い詳細度で不透明背景を明示する。
// --overview-surface-1はOverview配下でのみ定義され、未定義時(LegacyOverview)はテーマ既定色にフォールバック
.v-card.customer-detail-popover {
  background-color: var(--overview-surface-1, #ffffff) !important;
}

.theme--dark.v-card.customer-detail-popover {
  background-color: var(--overview-surface-1, #312d4b) !important;
}

.customer-detail-card__visit {
  line-height: 1.3;
}

.customer-detail-card__stat {
  flex: 1;
}

.caution-strip {
  display: flex;
  align-items: flex-start;
  padding: 6px 8px;
  border-radius: 4px;
  border-left: 3px solid rgba(244, 67, 54, 0.7);
  background: rgba(244, 67, 54, 0.12);
  font-size: 12px;
}

.caution-strip__icon {
  margin-top: 2px;
  margin-right: 6px;
  color: #ef5350 !important;
}

.caution-strip__content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.caution-strip__body {
  min-width: 0;
}

.caution-title {
  color: #ef5350;
  font-weight: 600;
}
</style>
